import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Checkbox,
  FormControlLabel,
} from '@mui/material';

interface EditGameModalProps {
  show: boolean;
  onHide: () => void;
  onEditGame: (game: any) => void;
  game: {
    id: string;
    title: string;
    year: number;
    image: string;
    platform: string;
    edition: string;
    editionName?: string;
    steelbook: boolean;
    sealed: boolean;
  };
}

function EditGameModal({
  show,
  onHide,
  onEditGame,
  game,
}: EditGameModalProps) {
  const [title, setTitle] = useState(game.title);
  const [year, setYear] = useState(game.year);
  const [platform, setPlatform] = useState(game.platform);
  const [edition, setEdition] = useState(game.edition);
  const [editionName, setEditionName] = useState(game.editionName || '');
  const [steelbook, setSteelbook] = useState(game.steelbook);
  const [sealed, setSealed] = useState(game.sealed);
  const [image, setImage] = useState(game.image);

  useEffect(() => {
    if (show) {
      setTitle(game.title);
      setYear(game.year);
      setPlatform(game.platform);
      setEdition(game.edition);
      setEditionName(game.editionName || '');
      setSteelbook(game.steelbook);
      setSealed(game.sealed);
      setImage(game.image);
    }
  }, [show, game]);

  const handleSaveChanges = useCallback(() => {
    onEditGame({
      ...game,
      title,
      year,
      platform,
      edition,
      editionName: edition === 'Special' ? editionName.trim() : '',
      steelbook,
      sealed,
      image,
    });

    onHide();
  }, [
    game,
    title,
    year,
    platform,
    edition,
    editionName,
    steelbook,
    sealed,
    image,
    onEditGame,
    onHide,
  ]);

  return (
    <Dialog open={show} onClose={onHide} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Game</DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <TextField
            label="Year"
            type="number"
            value={year}
            onChange={(e) => setYear(+e.target.value)}
          />

          <FormControl>
            <InputLabel>Platform</InputLabel>
            <Select
              value={platform}
              label="Platform"
              onChange={(e) => setPlatform(e.target.value)}
            >
              <MenuItem value="PS1">PS1</MenuItem>
              <MenuItem value="PS2">PS2</MenuItem>
              <MenuItem value="PS3">PS3</MenuItem>
              <MenuItem value="PS4">PS4</MenuItem>
              <MenuItem value="PS5">PS5</MenuItem>
              <MenuItem value="Switch">Switch</MenuItem>
              <MenuItem value="Switch 2">Switch 2</MenuItem>
              <MenuItem value="DS">DS</MenuItem>
              <MenuItem value="3DS">3DS</MenuItem>
              <MenuItem value="PC">PC</MenuItem>
            </Select>
          </FormControl>

          <FormControl>
            <InputLabel>Edition</InputLabel>
            <Select
              value={edition}
              label="Edition"
              onChange={(e) => {
                const value = e.target.value;
                setEdition(value);
                if (value !== 'Special') {
                  setEditionName('');
                }
              }}
            >
              <MenuItem value="Standard">Standard</MenuItem>
              <MenuItem value="Special">Special</MenuItem>
            </Select>
          </FormControl>

          {edition === 'Special' && (
            <TextField
              label="Edition Name"
              value={editionName}
              onChange={(e) => setEditionName(e.target.value)}
              placeholder="Collector's Edition"
            />
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={steelbook}
                  onChange={(e) => setSteelbook(e.target.checked)}
                />
              }
              label="Steelbook"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={sealed}
                  onChange={(e) => setSealed(e.target.checked)}
                />
              }
              label="Sealed"
            />
          </Box>

          <TextField
            label="Image URL"
            value={image}
            onChange={(e) => setImage(e.target.value)}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onHide}>Cancel</Button>
        <Button variant="contained" onClick={handleSaveChanges}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(EditGameModal);